import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { rememberStoreSettings } from "../../../util/storeSettings";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import {
  FaStore,
  FaTruck,
  FaCreditCard,
  FaGlobe,
  FaSave,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaImage,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaShareAlt,
  FaUndo,
  FaSitemap,
  FaRobot,
  FaShieldAlt,
  FaChartLine,
  FaCloudUploadAlt,
  FaCity,
  FaPlus,
  FaTrash,
  FaBroom,
  FaArrowRight
} from "react-icons/fa";
import { imageurl } from "../../../App";
import { setStoreFreeShippingThreshold } from "../../../util/guestCartService";
import { applyStoreFavicon } from "../../../util/storeLogoHelper";
import { focusFirstError, readApiErrors } from "../../../util/formErrors";
import "../../../assets/admin/css/store-settings-guide.css";

const DEFAULT_PAKISTAN_CITIES = [
  { id: "1", cityName: "Karachi", deliveryCharge: 150, estimatedDays: "1–2 Days", active: true },
  { id: "2", cityName: "Lahore", deliveryCharge: 200, estimatedDays: "2–3 Days", active: true },
  { id: "3", cityName: "Islamabad", deliveryCharge: 200, estimatedDays: "2–3 Days", active: true },
  { id: "4", cityName: "Rawalpindi", deliveryCharge: 200, estimatedDays: "2–3 Days", active: true },
  { id: "5", cityName: "Faisalabad", deliveryCharge: 220, estimatedDays: "2–4 Days", active: true },
  { id: "6", cityName: "Multan", deliveryCharge: 220, estimatedDays: "2–4 Days", active: true },
  { id: "7", cityName: "Peshawar", deliveryCharge: 250, estimatedDays: "3–4 Days", active: true },
  { id: "8", cityName: "Quetta", deliveryCharge: 290, estimatedDays: "3–5 Days", active: true },
  { id: "9", cityName: "Sialkot", deliveryCharge: 220, estimatedDays: "2–4 Days", active: true },
  { id: "10", cityName: "Gujranwala", deliveryCharge: 220, estimatedDays: "2–4 Days", active: true },
  { id: "11", cityName: "Hyderabad", deliveryCharge: 200, estimatedDays: "2–3 Days", active: true },
  { id: "12", cityName: "Sukkur", deliveryCharge: 250, estimatedDays: "3–4 Days", active: true },
  { id: "13", cityName: "Bahawalpur", deliveryCharge: 250, estimatedDays: "3–4 Days", active: true },
  { id: "14", cityName: "Sargodha", deliveryCharge: 250, estimatedDays: "3–4 Days", active: true },
  { id: "15", cityName: "Abbottabad", deliveryCharge: 250, estimatedDays: "3–4 Days", active: true },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    storeName: "Gootocart Store",
    storeTagline: "Premium Online Shopping Nationwide",
    logoUrl: "",
    faviconUrl: "",
    supportEmail: "",
    notificationEmail: "",
    supportPhone: "0332-2417001",
    supportHours: "Mon – Sat: 10:00 AM – 8:00 PM",
    warehouseAddress: "Shop G21, KN Gohar Society, Behind Malir Courts, Karachi, Pakistan",
    warehouseCity: "Karachi",
    country: "Pakistan",
    currency: "PKR",
    currencySymbol: "Rs.",
    taxNumber: "",
    freeShippingThreshold: 2500,
    shippingFee: 200,
    localShippingFee: 150,
    majorCitiesShippingFee: 200,
    regionalShippingFee: 290,
    expressShippingFee: 380,
    enableCOD: true,
    enableBankTransfer: true,
    bankDetails: "",
    facebookUrl: "https://facebook.com",
    instagramUrl: "https://instagram.com",
    twitterUrl: "https://twitter.com",
    linkedinUrl: "https://linkedin.com",
    whatsappNumber: "0332-2417001",
    metaTitle: "",
    metaDescription: "Shop luxury apparel, footwear, accessories, and essentials with nationwide fast delivery.",
    footerDescription: "Pakistan's premium online destination for authentic electronics, fashion, appliances, and lifestyle essentials. Resounding discounts, express delivery, and secure payments nationwide.",
    announcementBarEnabled: true,
    announcementBarText: "⚡ Free Express Delivery Nationwide on All Orders — Shop the New Season Collection",
    announcementBarLink: "/collection",
    announcementBarBg: "#111827",
    announcementBarColor: "#ffffff",
    announcementBarSpeed: "medium",
    // Shopify-style Auto SEO & Store Config defaults
    titleSeparator: "–",
    metaKeywords: "online shopping, best deals, cash on delivery, pakistan shopping",
    socialShareImage: "",
    googleSiteVerification: "",
    bingSiteVerification: "",
    gaMeasurementId: "",
    metaPixelEnabled: false,
    metaPixelId: "",
    metaConversionsApiEnabled: false,
    metaConversionsApiAccessToken: "",
    metaConversionsApiTestEventCode: "",
    metaConversionsApiTokenConfigured: false,
    enableRichSnippets: true,
    robotsIndexing: "index, follow",
    canonicalBaseUrl: "",
    cityDeliveryCharges: DEFAULT_PAKISTAN_CITIES,
  });

  const [newCity, setNewCity] = useState({
    cityName: "",
    deliveryCharge: 200,
    estimatedDays: "2–3 Days",
  });
  const [citySearchTerm, setCitySearchTerm] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  // Upload problems shown under each picture box (logo, favicon, social share image).
  const [uploadErrors, setUploadErrors] = useState({});
  const setUploadError = (field, message) => setUploadErrors((prev) => ({ ...prev, [field]: message }));
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoOptimizing, setLogoOptimizing] = useState(false);
  const [logoFileInfo, setLogoFileInfo] = useState("");

  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  const [socialShareFile, setSocialShareFile] = useState(null);
  const [socialSharePreview, setSocialSharePreview] = useState(null);
  const [socialShareOptimizing, setSocialShareOptimizing] = useState(false);
  const [socialShareFileInfo, setSocialShareFileInfo] = useState("");

  const [seoDiagnostics, setSeoDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [testingMetaCapi, setTestingMetaCapi] = useState(false);
  const [activeSettingsSection, setActiveSettingsSection] = useState("settings-identity");
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);

  const setupSteps = useMemo(() => {
    const hasText = (value) => String(value || "").trim().length > 0;
    const activeCities = Array.isArray(settings.cityDeliveryCharges)
      ? settings.cityDeliveryCharges.filter((city) => city.active !== false)
      : [];

    return [
      {
        id: "settings-identity",
        title: "Brand your store",
        description: "Set the customer-facing store name, logo, favicon, and footer introduction.",
        icon: FaStore,
        complete: hasText(settings.storeName) && (hasText(settings.logoUrl) || Boolean(logoFile)),
      },
      {
        id: "settings-contact",
        title: "Add contact details",
        description: "Tell customers how to reach you and where orders are dispatched from.",
        icon: FaPhoneAlt,
        complete: hasText(settings.supportEmail) && hasText(settings.supportPhone) && hasText(settings.warehouseAddress),
      },
      {
        id: "settings-currency",
        title: "Choose currency",
        description: "Confirm the currency, symbol, country, and optional business tax identity.",
        icon: FaMoneyBillWave,
        complete: hasText(settings.currency) && hasText(settings.currencySymbol) && hasText(settings.country),
      },
      {
        id: "settings-shipping",
        title: "Configure delivery",
        description: "Set the warehouse hub, free-shipping target, default fee, and active city rates.",
        icon: FaTruck,
        complete: hasText(settings.warehouseCity) && Number(settings.regionalShippingFee) >= 0 && activeCities.length > 0,
      },
      {
        id: "settings-payments",
        title: "Enable payments",
        description: "Choose cash on delivery or provide complete bank-transfer instructions.",
        icon: FaCreditCard,
        complete: Boolean(settings.enableCOD) || (Boolean(settings.enableBankTransfer) && hasText(settings.bankDetails)),
      },
      {
        id: "settings-seo",
        title: "Prepare for launch",
        description: "Complete the homepage title, description, sharing image, and search visibility.",
        icon: FaGlobe,
        complete: hasText(settings.metaTitle) && hasText(settings.metaDescription),
      },
      {
        id: "settings-marketing",
        title: "Connect Meta Pixel",
        description: "Connect browser Pixel tracking and secure server-side purchase measurement.",
        icon: FaChartLine,
        complete: Boolean(settings.metaPixelEnabled)
          && /^\d{5,20}$/.test(String(settings.metaPixelId || "").trim())
          && Boolean(settings.metaConversionsApiEnabled)
          && Boolean(settings.metaConversionsApiTokenConfigured),
      },
    ];
  }, [settings, logoFile]);

  const completedSetupSteps = setupSteps.filter((step) => step.complete).length;
  const setupProgress = Math.round((completedSetupSteps / setupSteps.length) * 100);
  const nextSetupStep = setupSteps.find((step) => !step.complete) || setupSteps[setupSteps.length - 1];

  const setupComplete = (id) => Boolean(setupSteps.find((step) => step.id === id)?.complete);
  const settingsNavigation = [
    { id: "settings-identity", label: "Brand & identity", hint: "Name, logo and favicon", icon: FaStore, complete: setupComplete("settings-identity") },
    { id: "settings-contact", label: "Contact details", hint: "Support and warehouse", icon: FaPhoneAlt, complete: setupComplete("settings-contact") },
    { id: "settings-currency", label: "Currency & region", hint: "Country and store currency", icon: FaMoneyBillWave, complete: setupComplete("settings-currency") },
    { id: "settings-shipping", label: "Shipping & delivery", hint: "Rates, cities and delivery", icon: FaTruck, complete: setupComplete("settings-shipping") },
    { id: "settings-payments", label: "Payment methods", hint: "COD and bank transfer", icon: FaCreditCard, complete: setupComplete("settings-payments") },
    {
      id: "settings-channels",
      label: "Social channels",
      hint: "WhatsApp and social profiles",
      icon: FaShareAlt,
      complete: Boolean(String(settings.whatsappNumber || settings.facebookUrl || settings.instagramUrl || "").trim()),
    },
    {
      id: "settings-seo",
      label: "SEO & discoverability",
      hint: "Search, sitemap and sharing",
      icon: FaGlobe,
      complete: setupComplete("settings-seo"),
    },
    {
      id: "settings-marketing",
      label: "Meta integration",
      hint: "Pixel and Conversions API",
      icon: FaChartLine,
      complete: setupComplete("settings-marketing"),
    },
  ];

  const goToSetupStep = (id) => {
    setActiveSettingsSection(id);
    window.setTimeout(() => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => target.querySelector("input, select, textarea, button")?.focus({ preventScroll: true }), 350);
    }, 60);
  };

  const settingsPanelClass = (id, base) =>
    `${base} settings-focus-panel ${activeSettingsSection === "all" || activeSettingsSection === id ? "is-active" : ""}`;

  const currencyPresets = [
    { code: "PKR", symbol: "Rs.", label: "PKR (Rs.)" },
    { code: "USD", symbol: "$", label: "USD ($)" },
    { code: "AED", symbol: "AED", label: "AED (د.إ)" },
    { code: "SAR", symbol: "SAR", label: "SAR (ر.س)" },
    { code: "GBP", symbol: "£", label: "GBP (£)" },
    { code: "EUR", symbol: "€", label: "EUR (€)" },
    { code: "CAD", symbol: "$", label: "CAD ($)" },
  ];

  const fetchSettings = () => {
    setLoading(true);
    axios
      .get("/api/admin/settings")
      .then((res) => {
        if (res.data.status === 200 && res.data.settings) {
          const loaded = res.data.settings;
          if (typeof loaded.cityDeliveryCharges === "string") {
            try {
              loaded.cityDeliveryCharges = JSON.parse(loaded.cityDeliveryCharges);
            } catch {
              loaded.cityDeliveryCharges = DEFAULT_PAKISTAN_CITIES;
            }
          }
          if (!Array.isArray(loaded.cityDeliveryCharges)) {
            loaded.cityDeliveryCharges = DEFAULT_PAKISTAN_CITIES;
          }
          setSettings(loaded);
          applyStoreFavicon(loaded);
          if (loaded.freeShippingThreshold !== undefined) {
            setStoreFreeShippingThreshold(loaded.freeShippingThreshold);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        axios.get("/api/settings").then((res) => {
          if (res.data.status === 200 && res.data.settings) {
            const loaded = res.data.settings;
            if (typeof loaded.cityDeliveryCharges === "string") {
              try {
                loaded.cityDeliveryCharges = JSON.parse(loaded.cityDeliveryCharges);
              } catch {
                loaded.cityDeliveryCharges = DEFAULT_PAKISTAN_CITIES;
              }
            }
            if (!Array.isArray(loaded.cityDeliveryCharges)) {
              loaded.cityDeliveryCharges = DEFAULT_PAKISTAN_CITIES;
            }
            setSettings(loaded);
            applyStoreFavicon(loaded);
            if (loaded.freeShippingThreshold !== undefined) {
              setStoreFreeShippingThreshold(loaded.freeShippingThreshold);
            }
          }
          setLoading(false);
        }).catch(() => {
          setLoading(false);
        });
      });

    // Fetch live SEO Diagnostics & Audit
    axios
      .get("/api/seo/diagnostics")
      .then((res) => {
        if (res.data.status === 200) {
          setSeoDiagnostics(res.data);
        }
      })
      .catch(() => {});
  };

  const clearCache = () => {
    swal({
      title: "Clear cache?",
      text: "This refreshes the store cache so the storefront loads the latest settings and theme data.",
      icon: "warning",
      buttons: ["Cancel", "Clear cache"],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      setClearingCache(true);
      axios
        .post("/api/admin/cache/clear")
        .then((res) => {
          setClearingCache(false);
          if (res.data.status === 200) {
            swal("Refreshed", res.data.message || "Store cache refreshed.", "success", {
              buttons: false,
              timer: 1400,
            });
          } else {
            swal("Notice", res.data.message || "Could not clear the cache.", "error");
          }
        })
        .catch(() => {
          setClearingCache(false);
          swal("Error", "Could not clear the cache.", "error");
        });
    });
  };

  const testMetaConversions = () => {
    setTestingMetaCapi(true);
    axios.post("/api/admin/settings/meta-conversions/test")
      .then((res) => {
        setTestingMetaCapi(false);
        swal("Server event received", res.data.message || "Meta received the test event.", "success");
      })
      .catch((error) => {
        setTestingMetaCapi(false);
        swal(
          "Connection test failed",
          error.response?.data?.message || "Save the integration settings and try again.",
          "error"
        );
      });
  };

  useEffect(() => {
    document.title = "Store Configuration & Settings — Admin";
    fetchSettings();
  }, []);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "storeName") {
      setSettings((prev) => {
        const previousName = (prev.storeName || "").trim();
        let metaTitle = prev.metaTitle || "";
        if (previousName && metaTitle.toLowerCase().includes(previousName.toLowerCase())) {
          const pattern = new RegExp(previousName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
          metaTitle = metaTitle.replace(pattern, value.trim() || "Store");
        }
        return { ...prev, storeName: value, metaTitle };
      });
      return;
    }
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Compress and resize images client-side before upload to prevent HTTP 413
  const compressImage = (file, maxWidth = 800, maxHeight = 400, quality = 0.85) => {
    return new Promise((resolve) => {
      if (file.type === "image/svg+xml" || file.size < 150 * 1024) {
        return resolve(file);
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            if (width / height > maxWidth / maxHeight) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          const isPng = file.type === "image/png";
          const mimeType = isPng ? "image/png" : "image/jpeg";

          canvas.toBlob(
            (blob) => {
              if (blob && blob.size < file.size) {
                const optimized = new File(
                  [blob],
                  file.name.replace(/\.[^.]+$/, isPng ? ".png" : ".jpg"),
                  { type: mimeType, lastModified: Date.now() }
                );
                resolve(optimized);
              } else {
                resolve(file);
              }
            },
            mimeType,
            quality
          );
        };
        img.onerror = () => resolve(file);
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!/^image\/(png|jpe?g|webp|gif|svg\+xml)$/i.test(file.type)) {
      setUploadError("logo", `"${file.name}" is not supported. Use a PNG, JPG, WebP, GIF or SVG logo.`);
      e.target.value = "";
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setUploadError("logo", `"${file.name}" is larger than 20 MB. Choose a smaller logo image.`);
      e.target.value = "";
      return;
    }
    setUploadError("logo", "");

    setLogoOptimizing(true);
    setLogoPreview(URL.createObjectURL(file));

    try {
      const optimized = await compressImage(file);
      setLogoFile(optimized);
      const sizeKb = Math.round(optimized.size / 1024);
      setLogoFileInfo(`${optimized.name} (${sizeKb} KB — Web Ready)`);
    } catch {
      setLogoFile(file);
      setLogoFileInfo(`${file.name} (${Math.round(file.size / 1024)} KB)`);
    } finally {
      setLogoOptimizing(false);
    }
  };

  const handleFaviconFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowed = file.type.startsWith("image/") || /\.ico$/i.test(file.name);
    if (!allowed) {
      setUploadError("favicon", `"${file.name}" is not supported. Use a PNG, JPG, WebP, SVG, GIF or ICO image.`);
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("favicon", `"${file.name}" is larger than 2 MB. The favicon must be under 2 MB.`);
      e.target.value = "";
      return;
    }
    setUploadError("favicon", "");

    setFaviconFile(file);
    setFaviconPreview(URL.createObjectURL(file));
  };

  const handleSocialShareUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpe?g|webp)$/i)) {
      setUploadError("socialShareImageFile", `"${file.name}" is not supported. The social share image must be PNG, JPG or WebP.`);
      e.target.value = "";
      return;
    }
    setUploadError("socialShareImageFile", "");

    setSocialShareOptimizing(true);
    setSocialSharePreview(URL.createObjectURL(file));

    try {
      const optimized = await compressImage(file, 1200, 630, 0.88);
      setSocialShareFile(optimized);
      const sizeKb = Math.round(optimized.size / 1024);
      setSocialShareFileInfo(`${optimized.name} (${sizeKb} KB — 1200x630 Optimized)`);
    } catch {
      setSocialShareFile(file);
      setSocialShareFileInfo(`${file.name} (${Math.round(file.size / 1024)} KB)`);
    } finally {
      setSocialShareOptimizing(false);
    }
  };

  const handleCurrencyPreset = (preset) => {
    setSettings((prev) => ({
      ...prev,
      currency: preset.code,
      currencySymbol: preset.symbol,
    }));
  };

  const handleAddCity = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newCity.cityName.trim()) {
      swal("Required", "Please enter a city name.", "warning");
      return;
    }
    const currentCities = Array.isArray(settings.cityDeliveryCharges) ? settings.cityDeliveryCharges : [];
    const exists = currentCities.some(
      (c) => (c.cityName || "").toLowerCase().trim() === newCity.cityName.toLowerCase().trim()
    );
    if (exists) {
      swal("Already Exists", `City "${newCity.cityName}" is already in the list. You can edit its charge in the table below.`, "info");
      return;
    }
    const newItem = {
      id: String(Date.now()),
      cityName: newCity.cityName.trim(),
      deliveryCharge: Number(newCity.deliveryCharge) || 0,
      estimatedDays: newCity.estimatedDays.trim() || "2–3 Days",
      active: true,
    };
    setSettings((prev) => ({
      ...prev,
      cityDeliveryCharges: [newItem, ...currentCities],
    }));
    setNewCity({ cityName: "", deliveryCharge: 200, estimatedDays: "2–3 Days" });
    swal("City Added", `Added "${newItem.cityName}" with Rs. ${newItem.deliveryCharge} delivery fee. Click "Save City Rates" to save changes.`, "success");
  };

  const handleDeleteCity = (id) => {
    const targetId = String(id);
    setSettings((prev) => ({
      ...prev,
      cityDeliveryCharges: (prev.cityDeliveryCharges || []).filter((c) => String(c.id) !== targetId),
    }));
  };

  const handleToggleCityActive = (id) => {
    const targetId = String(id);
    setSettings((prev) => ({
      ...prev,
      cityDeliveryCharges: (prev.cityDeliveryCharges || []).map((c) => {
        if (String(c.id) === targetId) {
          const currentActive = c.active !== false && c.active !== "false" && c.active !== 0 && c.active !== "0";
          return { ...c, active: !currentActive };
        }
        return c;
      }),
    }));
  };

  const handleCityFieldChange = (id, field, value) => {
    const targetId = String(id);
    setSettings((prev) => ({
      ...prev,
      cityDeliveryCharges: (prev.cityDeliveryCharges || []).map((c) =>
        String(c.id) === targetId ? { ...c, [field]: field === "deliveryCharge" ? Number(value) : value } : c
      ),
    }));
  };

  const handleResetStandardCities = () => {
    swal({
      title: "Reset Standard Cities?",
      text: "This will restore the standard 15 Pakistani major cities and rates.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Reset"],
      dangerMode: true,
    }).then((willReset) => {
      if (willReset) {
        setSettings((prev) => ({
          ...prev,
          cityDeliveryCharges: DEFAULT_PAKISTAN_CITIES,
        }));
        swal("Reset Complete", "Default cities restored. Click 'Save Store Configurations' to save.", "success");
      }
    });
  };

  const filteredCities = (settings.cityDeliveryCharges || []).filter((c) => {
    if (!citySearchTerm) return true;
    return (c.cityName || "").toLowerCase().includes(citySearchTerm.toLowerCase());
  });

  const handleSave = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setSaving(true);

    try {
      let requestPromise;

      if (logoFile || socialShareFile || faviconFile) {
        if (logoFile && logoFile.size > 4 * 1024 * 1024) {
          setSaving(false);
          swal("File Too Large", "The logo file exceeds 4MB. Please choose a smaller image or SVG.", "warning");
          return;
        }
        if (faviconFile && faviconFile.size > 2 * 1024 * 1024) {
          setSaving(false);
          swal("File Too Large", "The favicon must be under 2MB.", "warning");
          return;
        }

        const formData = new FormData();
        Object.keys(settings).forEach((key) => {
          if (key === "logoUrl" || key === "socialShareImage" || key === "faviconUrl") return;
          const val = settings[key];
          formData.append(key, typeof val === "object" ? JSON.stringify(val) : val);
        });
        if (!logoFile && settings.logoUrl) {
          formData.append("logoUrl", settings.logoUrl);
        }
        if (!socialShareFile && settings.socialShareImage) {
          formData.append("socialShareImage", settings.socialShareImage);
        }
        if (!faviconFile && settings.faviconUrl) {
          formData.append("faviconUrl", settings.faviconUrl);
        }
        if (logoFile) {
          formData.append("logo", logoFile);
        }
        if (faviconFile) {
          formData.append("favicon", faviconFile);
        }
        if (socialShareFile) {
          formData.append("socialShareImageFile", socialShareFile);
        }

        requestPromise = axios.post("/api/admin/settings", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // No file uploaded: Send standard JSON payload to prevent multipart overhead and 413
        requestPromise = axios.post("/api/admin/settings", settings);
      }

      const res = await requestPromise;
      setSaving(false);

      if (res.data.status === 200) {
        swal("Settings Saved", "Store configurations updated dynamically in database", "success", {
          buttons: false,
          timer: 1500,
        });
        if (res.data.settings) {
          const loaded = res.data.settings;
          if (typeof loaded.cityDeliveryCharges === "string") {
            try {
              loaded.cityDeliveryCharges = JSON.parse(loaded.cityDeliveryCharges);
            } catch {}
          }
          setSettings(loaded);
          applyStoreFavicon(loaded);
          rememberStoreSettings(loaded);
          setLogoPreview(null);
          setFaviconPreview(null);
          setSocialSharePreview(null);
          if (loaded.freeShippingThreshold !== undefined) {
            setStoreFreeShippingThreshold(loaded.freeShippingThreshold);
          }
        }
        setLogoFile(null);
        setLogoFileInfo("");
        setFaviconFile(null);
        setSocialShareFile(null);
        setSocialShareFileInfo("");
      } else {
        const { message, fields } = readApiErrors(res);
        setUploadErrors(fields);
        focusFirstError();
        swal("Settings not saved", message || "Failed to update settings", "error");
      }
    } catch (err) {
      setSaving(false);
      setUploadErrors(readApiErrors(err).fields);
      if (err.response && err.response.status === 413) {
        swal(
          "Payload Too Large (413)",
          "The uploaded logo image exceeds the server upload limit. The image has been auto-optimized for web; please try clicking Save again or choose an image under 2MB.",
          "error"
        );
      } else if (err.response && err.response.data && err.response.data.message) {
        swal("Save Failed", err.response.data.message, "error");
      } else {
        swal("Error", "We could not save your store settings. Please try again.", "error");
      }
    }
  };

  // Keyboard shortcut: Ctrl+S / Cmd+S anywhere on page to instantly save store configuration
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [settings, logoFile, faviconFile, socialShareFile, saving]);

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 fw-semibold">Loading Store Configuration from Database...</p>
      </div>
    );
  }

  return (
    <div className="store-settings-page">
      {/* Header & Quick Action */}
      <div className="settings-page-header d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            Store Configuration & Settings
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Dynamically configure store identity, branding, contact info, operating currency, nationwide shipping, payments, and SEO for store creation.
          </p>
        </div>
        <div className="settings-page-header-actions d-flex align-items-center gap-2">
          <Link
            to="/"
            target="_blank"
            className="btn btn-outline-primary btn-sm rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
          >
            <FaExternalLinkAlt size={12} /> Live Storefront
          </Link>
          <button
            type="button"
            onClick={clearCache}
            disabled={clearingCache}
            className="btn btn-light btn-sm rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 border"
          >
            <FaBroom size={12} /> {clearingCache ? "Clearing..." : "Clear cache"}
          </button>
          <button
            type="button"
            onClick={fetchSettings}
            className="btn btn-light btn-sm rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 border"
          >
            <FaUndo size={12} /> Reload
          </button>
        </div>
      </div>

      <section className="store-setup-guide" aria-labelledby="store-setup-title">
        <div className="store-setup-guide-head">
          <div>
            <span className="store-setup-eyebrow">Guided store setup</span>
            <h5 id="store-setup-title">Launch with confidence</h5>
            <p>Complete these steps in order. Your storefront updates after you save.</p>
          </div>
          <div className="store-setup-progress-copy">
            <strong>{setupProgress}%</strong>
            <span>{completedSetupSteps} of {setupSteps.length} complete</span>
            <button type="button" onClick={() => setSetupGuideOpen((open) => !open)}>
              {setupGuideOpen ? "Hide guide" : "Show guide"}
            </button>
          </div>
        </div>
        <div className="store-setup-progress" aria-label={`${setupProgress}% setup complete`}>
          <span style={{ width: `${setupProgress}%` }} />
        </div>
        {setupGuideOpen && (
          <>
        <div className="store-setup-steps">
          {setupSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <button
                key={step.id}
                type="button"
                className={`store-setup-step ${step.complete ? "is-complete" : ""} ${nextSetupStep.id === step.id ? "is-next" : ""}`}
                onClick={() => goToSetupStep(step.id)}
              >
                <span className="store-setup-step-number">{step.complete ? <FaCheckCircle /> : index + 1}</span>
                <span className="store-setup-step-icon"><Icon /></span>
                <span className="store-setup-step-copy">
                  <strong>{step.title}</strong>
                  <small>{step.description}</small>
                </span>
                <FaArrowRight className="store-setup-step-arrow" />
              </button>
            );
          })}
        </div>
        <div className="store-setup-guide-foot">
          <span>{setupProgress === 100 ? "Your essential store setup is complete." : `Recommended next: ${nextSetupStep.title}`}</span>
          <button type="button" onClick={() => goToSetupStep(nextSetupStep.id)}>
            {setupProgress === 100 ? "Review setup" : "Continue setup"} <FaArrowRight />
          </button>
        </div>
          </>
        )}
      </section>

      {/* Quick Overview Summary Banner */}
      <div className="settings-store-summary p-3 bg-white rounded-3 border mb-4 shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div className="settings-store-summary-main d-flex align-items-center gap-3">
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "var(--admin-primary)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
            }}
          >
            <FaStore />
          </div>
          <div>
            <h6 className="fw-bold mb-0 text-dark">{settings.storeName || "My Store"}</h6>
            <small className="text-muted">
              {settings.storeTagline || "Dynamic Store Configuration Active"} &bull; {settings.warehouseCity}, {settings.country}
            </small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-primary px-3 py-2" style={{ fontSize: "12px" }}>
            Currency: {settings.currency} ({settings.currencySymbol})
          </span>
          {settings.enableCOD && (
            <span className="badge bg-success px-3 py-2" style={{ fontSize: "12px" }}>
              <FaCheckCircle className="me-1" /> COD Enabled
            </span>
          )}
          {settings.enableBankTransfer && (
            <span className="badge bg-info px-3 py-2" style={{ fontSize: "12px" }}>
              <FaCheckCircle className="me-1" /> Bank transfer
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="settings-workspace">
          <aside className="settings-workspace-nav" aria-label="Store settings sections">
            <div className="settings-workspace-nav-head">
              <span>Store settings</span>
              <small>Choose one area to edit</small>
            </div>
            <div className="settings-workspace-nav-list">
              {settingsNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={activeSettingsSection === item.id ? "is-active" : ""}
                    onClick={() => goToSetupStep(item.id)}
                  >
                    <span className="settings-workspace-nav-icon"><Icon /></span>
                    <span className="settings-workspace-nav-copy"><strong>{item.label}</strong><small>{item.hint}</small></span>
                    <span className={`settings-workspace-nav-state ${item.complete ? "is-complete" : ""}`}>
                      {item.complete ? <FaCheckCircle /> : <FaArrowRight />}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`settings-show-all ${activeSettingsSection === "all" ? "is-active" : ""}`}
              onClick={() => setActiveSettingsSection("all")}
            >
              View all settings
            </button>
            <div className="settings-workspace-save-card">
              <span>{setupProgress}% complete</span>
              <button type="submit" disabled={saving}><FaSave /> {saving ? "Saving…" : "Save changes"}</button>
            </div>
          </aside>

          <div className="settings-workspace-content">
          <div className="row g-4">
          {/* 2. Store Profile, Branding & Identity (Dynamic for Store Creation) */}
          <div className={settingsPanelClass("settings-identity", "col-12 settings-guide-target settings-order-identity")} id="settings-identity">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaStore className="text-primary" /> Store Identity & Branding
                </h5>
              </div>
              <div className="admin-card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Store Display Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="storeName"
                    className="form-control fw-bold"
                    value={settings.storeName || ""}
                    onChange={handleInput}
                    placeholder="e.g. My Premium Store"
                    required
                  />
                  <small className="text-muted">Used in storefront header, page titles, footer, and emails.</small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Store Slogan / Tagline
                  </label>
                  <input
                    type="text"
                    name="storeTagline"
                    className="form-control"
                    value={settings.storeTagline || ""}
                    onChange={handleInput}
                    placeholder="e.g. Pakistan's Leading Online Shopping Destination"
                  />
                </div>

                {/* Store Logo Section */}
                <div className="p-3 bg-light rounded-3 border mb-3">
                  <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-2" style={{ fontSize: "13px" }}>
                    <FaImage className="text-primary" /> Store Logo
                  </label>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className="p-2 bg-white rounded border d-flex align-items-center justify-content-center"
                      style={{ width: "120px", height: "60px", overflow: "hidden" }}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                      ) : settings.logoUrl ? (
                        <img
                          src={settings.logoUrl.startsWith("http") ? settings.logoUrl : `${imageurl}/${settings.logoUrl}`}
                          alt="Store Logo"
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <span className="text-muted" style={{ fontSize: "11px" }}>Default Logo</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label fw-semibold mb-1" style={{ fontSize: "12px" }}>
                        Upload New Logo Image:
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control form-control-sm"
                        onChange={handleLogoFileChange}
                      />
                      {uploadErrors.logo && <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{uploadErrors.logo}</div>}
                      {logoOptimizing && (
                        <div className="mt-1 small text-primary fw-semibold">
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Optimizing image for web...
                        </div>
                      )}
                      {logoFileInfo && !logoOptimizing && (
                        <div className="mt-1 small text-success fw-semibold">
                          ✓ {logoFileInfo}
                        </div>
                      )}
                      {!logoFileInfo && !logoOptimizing && (
                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                          PNG, JPG, SVG or WEBP (auto-optimized for web). Max 4MB.
                        </small>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: "12px" }}>
                      Or Provide Custom Logo URL:
                    </label>
                    <input
                      type="text"
                      name="logoUrl"
                      className="form-control form-control-sm"
                      value={settings.logoUrl || ""}
                      onChange={handleInput}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>

                <div className="p-3 bg-light rounded-3 border mb-3">
                  <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-2" style={{ fontSize: "13px" }}>
                    <FaImage className="text-primary" /> Favicon
                  </label>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className="p-2 bg-white rounded border d-flex align-items-center justify-content-center"
                      style={{ width: "60px", height: "60px", overflow: "hidden" }}
                    >
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="Favicon preview" style={{ width: "32px", height: "32px", objectFit: "contain" }} />
                      ) : settings.faviconUrl ? (
                        <img
                          src={settings.faviconUrl.startsWith("http") ? settings.faviconUrl : `${imageurl}/${settings.faviconUrl}`}
                          alt="Store favicon"
                          style={{ width: "32px", height: "32px", objectFit: "contain" }}
                        />
                      ) : settings.logoUrl ? (
                        <img
                          src={settings.logoUrl.startsWith("http") ? settings.logoUrl : `${imageurl}/${settings.logoUrl}`}
                          alt="Store logo used as favicon"
                          style={{ width: "32px", height: "32px", objectFit: "contain" }}
                        />
                      ) : (
                        <span className="text-muted" style={{ fontSize: "10px" }}>Logo</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label fw-semibold mb-1" style={{ fontSize: "12px" }}>
                        Upload browser tab icon:
                      </label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.ico"
                        className="form-control form-control-sm"
                        onChange={handleFaviconFileChange}
                      />
                      {uploadErrors.favicon && <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{uploadErrors.favicon}</div>}
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                        PNG, JPG, WebP, SVG, GIF, or ICO. Square image works best. If empty, the store logo is used.
                      </small>
                    </div>
                  </div>
                  <div>
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: "12px" }}>
                      Or provide a favicon URL:
                    </label>
                    <input
                      type="text"
                      name="faviconUrl"
                      className="form-control form-control-sm"
                      value={settings.faviconUrl || ""}
                      onChange={handleInput}
                      placeholder="https://example.com/favicon.png"
                    />
                  </div>
                </div>

                {/* Footer Bio / Description */}
                <div>
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Store Bio / Footer About Description
                  </label>
                  <textarea
                    rows="3"
                    name="footerDescription"
                    className="form-control"
                    value={settings.footerDescription || ""}
                    onChange={handleInput}
                    placeholder="Brief 2-3 sentence overview displayed on the storefront footer..."
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Contact, Support & Physical Origin */}
          <div className={settingsPanelClass("settings-contact", "col-12 settings-guide-target settings-order-contact")} id="settings-contact">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaPhoneAlt className="text-primary" /> Support, Contact & Origin
                </h5>
              </div>
              <div className="admin-card-body">
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                      <FaEnvelope size={12} className="text-muted" /> Customer Support Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      name="supportEmail"
                      className="form-control"
                      value={settings.supportEmail || ""}
                      onChange={handleInput}
                      placeholder="support@yourstore.com"
                      required
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                      <FaPhoneAlt size={12} className="text-muted" /> Support Phone / WhatsApp <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="supportPhone"
                      className="form-control"
                      value={settings.supportPhone || ""}
                      onChange={handleInput}
                      placeholder="0300-1234567"
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                    <FaEnvelope size={12} className="text-muted" /> Store notification email
                  </label>
                  <input
                    type="email"
                    name="notificationEmail"
                    className="form-control"
                    value={settings.notificationEmail || ""}
                    onChange={handleInput}
                    placeholder="orders@yourstore.com"
                  />
                  <small className="text-muted">A copy of every email the store sends is delivered here. Leave this blank to use the customer support email.</small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                    <FaClock size={12} className="text-muted" /> Support Operating Hours
                  </label>
                  <input
                    type="text"
                    name="supportHours"
                    className="form-control"
                    value={settings.supportHours || ""}
                    onChange={handleInput}
                    placeholder="Mon – Sat: 10:00 AM – 8:00 PM"
                  />
                  <small className="text-muted">Displayed on the Contact Us page and customer notifications.</small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                    <FaMapMarkerAlt size={12} className="text-muted" /> Physical Retail / Warehouse Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="warehouseAddress"
                    className="form-control"
                    value={settings.warehouseAddress || ""}
                    onChange={handleInput}
                    placeholder="Shop G21, Main Commercial Area, City"
                    required
                  />
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      City / Hub Origin
                    </label>
                    <input
                      type="text"
                      name="warehouseCity"
                      className="form-control"
                      value={settings.warehouseCity || "Karachi"}
                      onChange={handleInput}
                      placeholder="Karachi"
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      className="form-control"
                      value={settings.country || "Pakistan"}
                      onChange={handleInput}
                      placeholder="Pakistan"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Operating Currency & Localization */}
          <div className={settingsPanelClass("settings-currency", "col-12 settings-guide-target settings-order-currency")} id="settings-currency">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaMoneyBillWave className="text-primary" /> Operating Currency & Localization
                </h5>
              </div>
              <div className="admin-card-body">
                {/* Currency Presets */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Select Currency Preset:
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {currencyPresets.map((preset) => (
                      <button
                        key={preset.code}
                        type="button"
                        className={`btn btn-sm rounded-pill ${
                          settings.currency === preset.code ? "btn-primary" : "btn-outline-secondary"
                        }`}
                        onClick={() => handleCurrencyPreset(preset)}
                        style={{ fontSize: "12px" }}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Currency ISO Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="currency"
                      className="form-control fw-bold"
                      value={settings.currency || "PKR"}
                      onChange={handleInput}
                      placeholder="PKR"
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Currency Symbol <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="currencySymbol"
                      className="form-control fw-bold"
                      value={settings.currencySymbol || "Rs."}
                      onChange={handleInput}
                      placeholder="Rs."
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Business NTN / Tax ID (Optional)
                  </label>
                  <input
                    type="text"
                    name="taxNumber"
                    className="form-control"
                    value={settings.taxNumber || ""}
                    onChange={handleInput}
                    placeholder="e.g. 1234567-8"
                  />
                  <small className="text-muted">Printed on customer receipts, invoices, and packing slips.</small>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Nationwide Shipping & Delivery Rules (Preserved intact as requested!) */}
          <div className={settingsPanelClass("settings-shipping", "col-12 settings-guide-target settings-order-shipping")} id="settings-shipping">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaTruck className="text-primary" /> Nationwide Shipping & Delivery
                </h5>
              </div>
              <div className="admin-card-body">
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Warehouse Hub City
                    </label>
                    <input
                      type="text"
                      name="warehouseCity"
                      className="form-control"
                      value={settings.warehouseCity || "Karachi"}
                      onChange={handleInput}
                      placeholder="e.g. Karachi"
                    />
                    <small className="text-muted">Origin city for local same-city shipping rate</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Free Shipping Order Threshold ({settings.currency || "PKR"})
                    </label>
                    <input
                      type="number"
                      name="freeShippingThreshold"
                      className="form-control fw-bold text-success"
                      value={settings.freeShippingThreshold ?? 2500}
                      onChange={handleInput}
                    />
                    <small className="text-muted">Orders at or above this amount get FREE shipping</small>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Default / Unlisted Cities Fee ({settings.currency || "PKR"})
                    </label>
                    <input
                      type="number"
                      name="regionalShippingFee"
                      className="form-control fw-bold"
                      value={settings.regionalShippingFee ?? 250}
                      onChange={handleInput}
                    />
                    <small className="text-muted">Standard rate applied for remote cities not listed in City Rates below</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Express Priority Delivery ({settings.currency || "PKR"})
                    </label>
                    <input
                      type="number"
                      name="expressShippingFee"
                      className="form-control"
                      value={settings.expressShippingFee ?? 380}
                      onChange={handleInput}
                    />
                    <small className="text-muted">Optional expedited delivery rate at checkout</small>
                  </div>
                </div>

                <div className="p-3 bg-light rounded-3 border">
                  <span className="fw-semibold d-block text-dark" style={{ fontSize: "13px" }}>
                    🚚 Dynamic City Delivery Model Active
                  </span>
                  <small className="text-muted">
                    Exact delivery charges are determined per city in the <strong>Dynamic City Delivery Rates</strong> table below. Any city not listed or toggled Off will automatically receive the default rate above.
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* 6. Payment Gateways & Methods */}
          <div className={settingsPanelClass("settings-payments", "col-12 settings-guide-target settings-order-payments")} id="settings-payments">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaCreditCard className="text-primary" /> Payment methods
                </h5>
              </div>
              <div className="admin-card-body">
                {/* COD Switch */}
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border mb-3">
                  <div>
                    <span className="fw-bold d-block text-dark">Cash on Delivery (COD)</span>
                    <small className="text-muted">Allow customers to pay cash upon parcel delivery</small>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="enableCOD"
                      checked={Boolean(settings.enableCOD)}
                      onChange={handleInput}
                      style={{ cursor: "pointer", width: "40px", height: "20px" }}
                    />
                  </div>
                </div>

                {/* Bank Transfer Switch */}
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border mb-3">
                  <div>
                    <span className="fw-bold d-block text-dark">Direct Bank Transfer / Wire</span>
                    <small className="text-muted">Enable manual bank account payment instructions at checkout</small>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="enableBankTransfer"
                      checked={Boolean(settings.enableBankTransfer)}
                      onChange={handleInput}
                      style={{ cursor: "pointer", width: "40px", height: "20px" }}
                    />
                  </div>
                </div>

                {settings.enableBankTransfer && (
                  <div className="mb-0">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Bank Account Details for Customers:
                    </label>
                    <textarea
                      rows="3"
                      name="bankDetails"
                      className="form-control"
                      value={settings.bankDetails || ""}
                      onChange={handleInput}
                      placeholder="Bank Name: Meezan Bank&#10;Account Title: Store Name&#10;IBAN: PK00MEZN0000000000000000"
                    ></textarea>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dynamic City-Based Delivery Rates Card */}
          <div className={settingsPanelClass("settings-shipping", "col-12 settings-order-city-rates")}>
            <div className="admin-card mb-4">
              <div className="admin-card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="admin-card-title d-flex align-items-center gap-2 mb-1">
                    <FaCity className="text-primary" /> Dynamic City Delivery Rates (Per-City Delivery Charges)
                  </h5>
                  <small className="text-muted">
                    Configure custom delivery charges and estimated delivery days for individual cities in Pakistan. Customers selecting these cities at checkout will be charged these exact rates.
                  </small>
                </div>
                <div className="city-rate-header-actions d-flex align-items-center gap-2">
                  <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 py-2 px-3 fw-semibold">
                    {(settings.cityDeliveryCharges || []).filter((c) => c.active !== false && c.active !== "false" && c.active !== 0 && c.active !== "0").length} Active Cities
                  </span>
                  <button
                    type="button"
                    onClick={handleResetStandardCities}
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    title="Reset to default Pakistani major cities"
                  >
                    <FaUndo size={11} /> Reset Standard Cities
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-sm btn-primary d-flex align-items-center gap-1 shadow-sm px-3 fw-semibold"
                  >
                    <FaSave size={12} /> {saving ? "Saving..." : "Save City Rates"}
                  </button>
                </div>
              </div>
              <div className="admin-card-body">
                {/* Add New City Bar */}
                <div className="p-3 bg-light rounded-3 border mb-3">
                  <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
                    <FaPlus size={12} className="text-primary me-1" /> Add New City Rate
                  </h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-4">
                      <label className="form-label mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                        City Name
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. Hyderabad, Sialkot, Gilgit"
                        value={newCity.cityName}
                        onChange={(e) => setNewCity((prev) => ({ ...prev, cityName: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCity(e);
                          }
                        }}
                      />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                        Delivery Charge ({settings.currency || "PKR"})
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm fw-bold"
                        placeholder="e.g. 200"
                        value={newCity.deliveryCharge}
                        onChange={(e) => setNewCity((prev) => ({ ...prev, deliveryCharge: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCity(e);
                          }
                        }}
                      />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                        Estimated Delivery Days
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. 2–3 Days"
                        value={newCity.estimatedDays}
                        onChange={(e) => setNewCity((prev) => ({ ...prev, estimatedDays: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCity(e);
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-2">
                      <button
                        type="button"
                        onClick={handleAddCity}
                        className="btn btn-sm btn-primary w-100 d-flex align-items-center justify-content-center gap-1 py-1"
                        style={{ height: "31px" }}
                      >
                        <FaPlus size={11} /> Add City
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search & Filter Header */}
                <div className="city-rate-filter d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>
                    Configured Cities ({(settings.cityDeliveryCharges || []).length})
                  </span>
                  <div style={{ maxWidth: "240px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search city..."
                      value={citySearchTerm}
                      onChange={(e) => setCitySearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Cities Table */}
                <div className="table-responsive rounded-3 border" style={{ maxHeight: "380px", overflowY: "auto" }}>
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: "35%" }}>City Name</th>
                        <th style={{ width: "25%" }}>Delivery Charge ({settings.currency || "PKR"})</th>
                        <th style={{ width: "25%" }}>Estimated Delivery</th>
                        <th style={{ width: "15%" }} className="text-end">Status & Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCities.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-muted">
                            {citySearchTerm ? `No cities found matching "${citySearchTerm}"` : "No cities configured yet. Click 'Reset Standard Cities' or add one above."}
                          </td>
                        </tr>
                      ) : (
                        filteredCities.map((c) => {
                          const isHub = (settings.warehouseCity || "Karachi").toLowerCase().trim() === (c.cityName || "").toLowerCase().trim();
                          const isActive = c.active !== false && c.active !== "false" && c.active !== 0 && c.active !== "0";
                          return (
                            <tr key={String(c.id)} className={!isActive ? "table-light opacity-75" : ""}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm fw-semibold"
                                    style={{ maxWidth: "180px" }}
                                    value={c.cityName}
                                    onChange={(e) => handleCityFieldChange(c.id, "cityName", e.target.value)}
                                  />
                                  {isHub && (
                                    <span className="badge bg-info text-dark" style={{ fontSize: "10px" }}>
                                      Warehouse Hub
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="input-group input-group-sm" style={{ maxWidth: "140px" }}>
                                  <span className="input-group-text">{settings.currencySymbol || "Rs."}</span>
                                  <input
                                    type="number"
                                    className="form-control fw-bold text-primary"
                                    value={c.deliveryCharge}
                                    onChange={(e) => handleCityFieldChange(c.id, "deliveryCharge", e.target.value)}
                                  />
                                </div>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ maxWidth: "160px" }}
                                  value={c.estimatedDays || ""}
                                  onChange={(e) => handleCityFieldChange(c.id, "estimatedDays", e.target.value)}
                                  placeholder="e.g. 2–3 Days"
                                />
                              </td>
                              <td className="text-end">
                                <div className="d-flex align-items-center justify-content-end gap-2">
                                  <div className="d-flex align-items-center gap-1">
                                    <div className="form-check form-switch m-0" title={isActive ? "Click to turn OFF" : "Click to turn ON"}>
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={isActive}
                                        onChange={() => handleToggleCityActive(c.id)}
                                        style={{ cursor: "pointer", width: "34px", height: "18px" }}
                                      />
                                    </div>
                                    <span
                                      className={`badge ${
                                        isActive
                                          ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                                          : "bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25"
                                      }`}
                                      style={{ fontSize: "10px", width: "45px", textAlign: "center" }}
                                    >
                                      {isActive ? "Active" : "Off"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteCity(c.id)}
                                    className="btn btn-sm btn-outline-danger p-1 px-2"
                                    title="Delete city"
                                  >
                                    <FaTrash size={11} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-2 text-muted" style={{ fontSize: "12px" }}>
                  💡 <em>Note: Customers who enter a city NOT listed above or toggled <strong>Off</strong> will automatically receive the <strong>Default Rate ({settings.currencySymbol || "Rs."} {settings.regionalShippingFee ?? 250})</strong>.</em>
                </div>
              </div>
            </div>
          </div>

          {/* 7. Social Media Channels */}
          <div className={settingsPanelClass("settings-channels", "col-12 settings-guide-target settings-order-channels")} id="settings-channels">
            <div className="admin-card mb-4 h-100">
              <div className="admin-card-header">
                <h5 className="admin-card-title d-flex align-items-center gap-2">
                  <FaShareAlt className="text-primary" /> Social Media & Channels
                </h5>
              </div>
              <div className="admin-card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Facebook Page URL
                  </label>
                  <input
                    type="url"
                    name="facebookUrl"
                    className="form-control"
                    value={settings.facebookUrl || ""}
                    onChange={handleInput}
                    placeholder="https://facebook.com/yourstore"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Instagram Profile URL
                  </label>
                  <input
                    type="url"
                    name="instagramUrl"
                    className="form-control"
                    value={settings.instagramUrl || ""}
                    onChange={handleInput}
                    placeholder="https://instagram.com/yourstore"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    Twitter / X Profile URL
                  </label>
                  <input
                    type="url"
                    name="twitterUrl"
                    className="form-control"
                    value={settings.twitterUrl || ""}
                    onChange={handleInput}
                    placeholder="https://x.com/yourstore"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    LinkedIn Page URL
                  </label>
                  <input
                    type="url"
                    name="linkedinUrl"
                    className="form-control"
                    value={settings.linkedinUrl || ""}
                    onChange={handleInput}
                    placeholder="https://linkedin.com/company/yourstore"
                  />
                </div>

                <div>
                  <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                    WhatsApp Chat Number / Link
                  </label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    className="form-control"
                    value={settings.whatsappNumber || ""}
                    onChange={handleInput}
                    placeholder="0300-1234567 or https://wa.me/923001234567"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 8. Shopify-Style Auto On-Page SEO, Discoverability & Webmaster Settings */}
          <div className={settingsPanelClass("settings-seo", "col-12 settings-guide-target settings-order-seo")} id="settings-seo">
            <div className="admin-card mb-4 border shadow-sm">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <FaGlobe className="text-primary" />
                  <h5 className="admin-card-title mb-0 fw-bold">
                    On-page SEO and discoverability
                  </h5>
                  <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 ms-2" style={{ fontSize: "11px" }}>
                    Automated Fallbacks Active
                  </span>
                </div>
                {seoDiagnostics && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border px-3 py-1.5 fw-semibold" style={{ fontSize: "12px" }}>
                      SEO Health: <strong className="text-success">{seoDiagnostics.health_score}%</strong>
                    </span>
                    <span className="badge bg-light text-muted border px-2.5 py-1.5" style={{ fontSize: "11.5px" }}>
                      {seoDiagnostics.stats?.sitemap_urls_count || 0} Indexed URLs
                    </span>
                  </div>
                )}
              </div>

              <div className="admin-card-body p-4">
                {/* Real-time Diagnostics Banner */}
                {seoDiagnostics && (
                  <div className="p-3 mb-4 rounded-3 border bg-light bg-opacity-75 d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "#e8f0fe",
                          color: "#1a73e8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "20px",
                        }}
                      >
                        <FaChartLine />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0 text-dark">
                          Auto-SEO is active
                        </h6>
                        <small className="text-muted">
                          {seoDiagnostics.stats?.products_with_meta_title} products have custom titles &bull;{" "}
                          {seoDiagnostics.stats?.products_using_auto_seo} products using smart auto-generated on-page SEO
                        </small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Link
                        to="/admin/seo"
                        className="btn btn-sm btn-outline-dark rounded-pill px-3 py-1.5 fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        SEO audit
                      </Link>
                      <Link
                        to="/admin/seo/search-console"
                        className="btn btn-sm btn-outline-dark rounded-pill px-3 py-1.5 fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        Search Console
                      </Link>
                      <a
                        href="/sitemap.xml"
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1.5 d-inline-flex align-items-center gap-1.5 fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        <FaSitemap /> View Sitemap.xml <FaExternalLinkAlt size={10} />
                      </a>
                      <a
                        href="/robots.txt"
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1.5 d-inline-flex align-items-center gap-1.5 fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        <FaRobot /> View Robots.txt <FaExternalLinkAlt size={10} />
                      </a>
                    </div>
                  </div>
                )}

                <div className="row g-4">
                  {/* Subsection A: Title Formatting & Brand Separator */}
                  <div className="col-12">
                    <div className="p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <label className="form-label fw-bold mb-0 text-dark" style={{ fontSize: "13.5px" }}>
                          Global Brand Title Separator & Template Formatting
                        </label>
                        <small className="text-muted">
                          Used across all product & collection page titles automatically
                        </small>
                      </div>
                      <div className="row g-3 align-items-center">
                        <div className="col-12 col-md-4">
                          <label className="form-label text-muted small mb-1">Choose Title Separator:</label>
                          <div className="d-flex gap-2">
                            {["–", "|", "•", "-"].map((sep) => (
                              <button
                                key={sep}
                                type="button"
                                onClick={() => setSettings((prev) => ({ ...prev, titleSeparator: sep }))}
                                className={`btn btn-sm rounded-3 px-3 fw-bold ${
                                  (settings.titleSeparator || "–") === sep
                                    ? "btn-primary shadow-sm"
                                    : "btn-outline-secondary bg-white"
                                }`}
                                style={{ fontSize: "14px", minWidth: "42px" }}
                              >
                                {sep}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <label className="form-label text-muted small mb-1">Live Title Auto-Format Preview:</label>
                          <div className="p-2 bg-white rounded border small">
                            <span className="text-muted d-block" style={{ fontSize: "11px" }}>
                              Product Page: <strong className="text-dark">Wireless Earbuds {settings.titleSeparator || "–"} Sony {settings.titleSeparator || "–"} {settings.storeName || "My Store"}</strong>
                            </span>
                            <span className="text-muted d-block mt-0.5" style={{ fontSize: "11px" }}>
                              Collection Page: <strong className="text-dark">Electronics Collection {settings.titleSeparator || "–"} {settings.storeName || "My Store"}</strong>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subsection B: Homepage SEO & Google SERP Preview */}
                  <div className="col-12 col-lg-7">
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label fw-semibold text-dark small mb-0">
                          Homepage Title Tag <span className="text-danger">*</span>
                        </label>
                        <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                          {(settings.metaTitle || "").length} / 60 chars
                        </span>
                      </div>
                      <input
                        type="text"
                        name="metaTitle"
                        className="form-control"
                        value={settings.metaTitle || ""}
                        onChange={handleInput}
                        placeholder="e.g. MyStore — Premium Online Shopping in Pakistan"
                        required
                      />
                      <small className="text-muted">
                        Shown in browser tab and search engine results for your store&apos;s home page.
                      </small>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label fw-semibold text-dark small mb-0">
                          Homepage Meta Description
                        </label>
                        <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                          {(settings.metaDescription || "").length} / 160 chars
                        </span>
                      </div>
                      <textarea
                        rows="3"
                        name="metaDescription"
                        className="form-control"
                        value={settings.metaDescription || ""}
                        onChange={handleInput}
                        placeholder="Describe your store in 140-160 characters to encourage search clicks..."
                      ></textarea>
                      <small className="text-muted">
                        Search snippet summary displayed below the title in Google search results.
                      </small>
                    </div>

                    <div className="mb-0">
                      <label className="form-label fw-semibold text-dark small mb-1">
                        Homepage Default Meta Keywords
                      </label>
                      <input
                        type="text"
                        name="metaKeywords"
                        className="form-control"
                        value={settings.metaKeywords || ""}
                        onChange={handleInput}
                        placeholder="e.g. online shopping, electronics, fashion, cash on delivery, pakistan"
                      />
                      <small className="text-muted">Comma-separated keywords for discoverability.</small>
                    </div>
                  </div>

                  {/* Subsection C: Google SERP Live Snippet Card */}
                  <div className="col-12 col-lg-5">
                    <label className="form-label fw-semibold text-dark small mb-2">
                      Live Google Search Snippet Preview:
                    </label>
                    <div
                      className="p-3 bg-white rounded border shadow-sm"
                      style={{
                        boxShadow: "0 1px 6px rgba(32,33,36,0.12)",
                        maxWidth: "480px",
                        fontFamily: "arial, sans-serif",
                      }}
                    >
                      <div className="d-flex align-items-center gap-1.5 text-muted small mb-1" style={{ fontSize: "12px" }}>
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: "#1a73e8",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "9px",
                            fontWeight: "bold",
                          }}
                        >
                          G
                        </div>
                        <span className="text-truncate" style={{ color: "#202124" }}>
                          {settings.canonicalBaseUrl || "https://yourstore.com"}
                        </span>
                      </div>
                      <h6
                        className="mb-1 text-truncate fw-semibold"
                        style={{ color: "#1a0dab", cursor: "pointer", fontSize: "16.5px" }}
                      >
                        {settings.metaTitle || `${settings.storeName || "My Store"} ${settings.titleSeparator || "–"} Online Store`}
                      </h6>
                      <p className="mb-0 text-muted" style={{ fontSize: "13px", lineHeight: "1.4", color: "#4d5156" }}>
                        {settings.metaDescription || "Shop authentic electronics, fashion, lifestyle essentials, and more with fast nationwide delivery."}
                      </p>
                    </div>

                    {/* Canonical URL Setting */}
                    <div className="mt-3">
                      <label className="form-label fw-semibold text-dark small mb-1">
                        Canonical Base Storefront URL
                      </label>
                      <input
                        type="url"
                        name="canonicalBaseUrl"
                        className="form-control form-control-sm"
                        value={settings.canonicalBaseUrl || ""}
                        onChange={handleInput}
                        placeholder="https://yourstore.com"
                      />
                      <small className="text-muted" style={{ fontSize: "11px" }}>
                        Prevents duplicate content penalties by enforcing primary store URL in sitemaps & canonical tags.
                      </small>
                    </div>
                  </div>

                  {/* Subsection D: Social Share Image (OpenGraph / WhatsApp / Facebook Preview) */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 bg-light rounded-3 border h-100">
                      <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <FaImage className="text-primary" /> Social Sharing Image (Default OG Image)
                      </h6>
                      <p className="text-muted small mb-3" style={{ fontSize: "12px" }}>
                        Shown when your homepage or pages without a custom image are shared on WhatsApp, Facebook, Twitter, and iMessage (Recommended: 1200 x 630 px).
                      </p>

                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 border bg-white overflow-hidden d-flex align-items-center justify-content-center"
                          style={{ width: "120px", height: "70px", flexShrink: 0 }}
                        >
                          {socialSharePreview ? (
                            <img src={socialSharePreview} alt="Social Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : settings.socialShareImage ? (
                            <img
                              src={settings.socialShareImage.startsWith("http") ? settings.socialShareImage : `${imageurl}${settings.socialShareImage}`}
                              alt="Social Share"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <span className="text-muted small" style={{ fontSize: "11px" }}>No Image</span>
                          )}
                        </div>

                        <div>
                          <label className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-2 cursor-pointer mb-1">
                            <FaCloudUploadAlt /> Choose Social Image
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              className="d-none"
                              onChange={handleSocialShareUpload}
                            />
                          </label>
                          {uploadErrors.socialShareImageFile && <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{uploadErrors.socialShareImageFile}</div>}
                          {socialShareFileInfo && (
                            <small className="text-success d-block fw-semibold" style={{ fontSize: "11px" }}>
                              {socialShareFileInfo}
                            </small>
                          )}
                          {socialShareOptimizing && (
                            <small className="text-primary d-block" style={{ fontSize: "11px" }}>
                              Optimizing for social networks...
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subsection E: Search Engine Indexing & Crawling Switch */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 bg-light rounded-3 border h-100">
                      <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <FaRobot className="text-primary" /> Search Engine Indexing & Robots Policy
                      </h6>
                      <p className="text-muted small mb-3" style={{ fontSize: "12px" }}>
                        Control whether search engine crawlers (Google, Bing) are allowed to index your store catalog.
                      </p>

                      <div className="form-check form-switch mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="robotsIndexingSwitch"
                          checked={settings.robotsIndexing === "index, follow" || settings.robotsIndexing === true}
                          onChange={(e) =>
                            setSettings((prev) => ({
                              ...prev,
                              robotsIndexing: e.target.checked ? "index, follow" : "noindex, nofollow",
                            }))
                          }
                          style={{ cursor: "pointer", width: "40px", height: "20px" }}
                        />
                        <label className="form-check-label fw-semibold text-dark ms-2" htmlFor="robotsIndexingSwitch">
                          {settings.robotsIndexing === "index, follow" || settings.robotsIndexing === true
                            ? "Allow search engines to index this store (Live Mode)"
                            : "Discourage search engines from indexing (Staging / Private)"}
                        </label>
                      </div>

                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="richSnippetsSwitch"
                          checked={Boolean(settings.enableRichSnippets)}
                          onChange={handleInput}
                          name="enableRichSnippets"
                          style={{ cursor: "pointer", width: "40px", height: "20px" }}
                        />
                        <label className="form-check-label fw-semibold text-dark ms-2" htmlFor="richSnippetsSwitch">
                          Enable Schema.org JSON-LD Rich Snippets (Product, Breadcrumbs, Store)
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Subsection F: Webmaster Verification Tags */}
                  <div className="col-12">
                    <div className="p-3 bg-light rounded-3 border">
                      <h6 className="fw-bold mb-2 text-dark d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                        <FaShieldAlt className="text-primary" /> Webmaster Verification Codes & Analytics
                      </h6>
                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold text-dark small mb-1">
                            Google Search Console Verification Tag
                          </label>
                          <input
                            type="text"
                            name="googleSiteVerification"
                            className="form-control form-control-sm font-monospace"
                            value={settings.googleSiteVerification || ""}
                            onChange={handleInput}
                            placeholder="e.g. AbC123dEf_xYz..."
                          />
                          <small className="text-muted" style={{ fontSize: "11px" }}>
                            Automatically injects &lt;meta name=&quot;google-site-verification&quot;&gt;
                          </small>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold text-dark small mb-1">
                            Bing Webmaster Verification Code
                          </label>
                          <input
                            type="text"
                            name="bingSiteVerification"
                            className="form-control form-control-sm font-monospace"
                            value={settings.bingSiteVerification || ""}
                            onChange={handleInput}
                            placeholder="e.g. 7D28E6..."
                          />
                          <small className="text-muted" style={{ fontSize: "11px" }}>
                            Automatically injects &lt;meta name=&quot;msvalidate.01&quot;&gt;
                          </small>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold text-dark small mb-1">
                            Google Analytics 4 (GA4) Measurement ID
                          </label>
                          <input
                            type="text"
                            name="gaMeasurementId"
                            className="form-control form-control-sm font-monospace"
                            value={settings.gaMeasurementId || ""}
                            onChange={handleInput}
                            placeholder="e.g. G-XXXXXXXXXX"
                          />
                          <small className="text-muted" style={{ fontSize: "11px" }}>
                            Stream ID for Google Analytics traffic tracking.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* 9. Meta advertising and server-side measurement */}
          <div className={settingsPanelClass("settings-marketing", "col-12 settings-guide-target settings-order-marketing")} id="settings-marketing">
            <div className="meta-integration-intro">
              <div className="meta-integration-intro-icon"><FaChartLine /></div>
              <div>
                <span>Sales channel</span>
                <h5>Meta advertising integration</h5>
                <p>Connect Facebook and Instagram measurement separately from your search-engine settings.</p>
              </div>
              <div className="meta-integration-summary">
                <span>{settings.metaPixelEnabled && /^\d{5,20}$/.test(String(settings.metaPixelId || "").trim()) ? "Pixel ready" : "Pixel setup needed"}</span>
                <span>{settings.metaConversionsApiEnabled && settings.metaConversionsApiTokenConfigured ? "Server ready" : "Server setup needed"}</span>
              </div>
            </div>

                    <div className={`meta-pixel-card ${settings.metaPixelEnabled && /^\d{5,20}$/.test(String(settings.metaPixelId || "").trim()) ? "is-connected" : ""}`}>
                      <div className="meta-pixel-card-head">
                        <div className="meta-pixel-brand">
                          <span className="meta-pixel-mark">M</span>
                          <div>
                            <h6>Meta Pixel</h6>
                            <p>Measure customer actions and improve Facebook and Instagram ad campaigns.</p>
                          </div>
                        </div>
                        <span className="meta-pixel-status">
                          {settings.metaPixelEnabled && /^\d{5,20}$/.test(String(settings.metaPixelId || "").trim())
                            ? "Connected"
                            : "Not connected"}
                        </span>
                      </div>

                      <div className="meta-pixel-layout">
                        <div className="meta-pixel-fields">
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="metaPixelEnabled"
                              name="metaPixelEnabled"
                              checked={Boolean(settings.metaPixelEnabled)}
                              onChange={handleInput}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="metaPixelEnabled">
                              Enable Meta Pixel on the live storefront
                            </label>
                          </div>
                          <label className="form-label fw-semibold small" htmlFor="metaPixelId">
                            Meta Pixel ID
                          </label>
                          <input
                            id="metaPixelId"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            name="metaPixelId"
                            className={`form-control font-monospace ${settings.metaPixelId && !/^\d{5,20}$/.test(String(settings.metaPixelId).trim()) ? "is-invalid" : ""}`}
                            value={settings.metaPixelId || ""}
                            onChange={handleInput}
                            placeholder="e.g. 123456789012345"
                            maxLength="20"
                          />
                          <small className="text-muted d-block mt-2">
                            Enter the numeric ID only. Theme demos and the theme editor never send tracking events.
                          </small>
                          {settings.metaPixelId && !/^\d{5,20}$/.test(String(settings.metaPixelId).trim()) && (
                            <div className="invalid-feedback">Enter a valid numeric Meta Pixel ID.</div>
                          )}
                        </div>

                        <div className="meta-pixel-guide">
                          <div className="meta-pixel-guide-title">
                            <div>
                              <strong>Connect in about 5 minutes</strong>
                              <span>No code installation is required.</span>
                            </div>
                            <a
                              href="https://business.facebook.com/events_manager2/list/pixel/"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open Events Manager <FaExternalLinkAlt />
                            </a>
                          </div>

                          <div className="meta-pixel-process">
                            <div className="meta-pixel-process-step">
                              <b>1</b>
                              <div><strong>Create the data source</strong><p>In Events Manager choose <em>Connect data</em>, select <em>Web</em>, and continue.</p></div>
                            </div>
                            <div className="meta-pixel-process-step">
                              <b>2</b>
                              <div><strong>Name and create it</strong><p>Enter your store name. Choose Meta Pixel if Meta asks for a connection method.</p></div>
                            </div>
                            <div className="meta-pixel-process-step">
                              <b>3</b>
                              <div><strong>Copy the ID</strong><p>Open the data source’s Settings. Copy the numeric Dataset ID or Pixel ID shown there.</p></div>
                            </div>
                            <div className="meta-pixel-process-step">
                              <b>4</b>
                              <div><strong>Connect this store</strong><p>Paste that ID in the field, switch tracking on, then click Save and activate.</p></div>
                            </div>
                            <div className="meta-pixel-process-step">
                              <b>5</b>
                              <div><strong>Test the connection</strong><p>In Meta’s Test events tab, open your live store and view a product, add it to cart, and start checkout.</p></div>
                            </div>
                          </div>

                          <div className="meta-pixel-events">
                            <span><FaCheckCircle /> Page views</span>
                            <span><FaCheckCircle /> Product views</span>
                            <span><FaCheckCircle /> Add to cart</span>
                            <span><FaCheckCircle /> Checkout</span>
                            <span><FaCheckCircle /> Purchases</span>
                          </div>

                          <div className="meta-pixel-guide-actions">
                            <a
                              href="https://www.facebook.com/business/help/628750084321697"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Read Meta’s setup guide <FaExternalLinkAlt />
                            </a>
                            <button
                              type="button"
                              onClick={handleSave}
                              disabled={saving || !settings.metaPixelEnabled || !/^\d{5,20}$/.test(String(settings.metaPixelId || "").trim())}
                            >
                              <FaSave /> {saving ? "Saving…" : "Save and activate"}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="meta-capi-panel">
                        <div className="meta-capi-heading">
                          <div>
                            <span className="meta-capi-kicker">Server-side tracking</span>
                            <h6>Meta Conversions API</h6>
                            <p>Your store securely sends confirmed purchases to Meta and prevents duplicate conversion reporting.</p>
                          </div>
                          <span className={`meta-capi-status ${settings.metaConversionsApiEnabled && settings.metaConversionsApiTokenConfigured ? "is-on" : ""}`}>
                            {settings.metaConversionsApiEnabled && settings.metaConversionsApiTokenConfigured ? "Server connected" : "Setup required"}
                          </span>
                        </div>

                        <div className="meta-capi-grid">
                          <div className="meta-capi-config">
                            <div className="form-check form-switch mb-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="metaConversionsApiEnabled"
                                name="metaConversionsApiEnabled"
                                checked={Boolean(settings.metaConversionsApiEnabled)}
                                onChange={handleInput}
                              />
                              <label className="form-check-label fw-semibold" htmlFor="metaConversionsApiEnabled">
                                Enable server-side Purchase events
                              </label>
                            </div>

                            <label className="form-label fw-semibold small" htmlFor="metaConversionsApiAccessToken">
                              Conversions API access token
                            </label>
                            <input
                              id="metaConversionsApiAccessToken"
                              type="password"
                              name="metaConversionsApiAccessToken"
                              className="form-control font-monospace"
                              value={settings.metaConversionsApiAccessToken || ""}
                              onChange={handleInput}
                              autoComplete="new-password"
                              placeholder={settings.metaConversionsApiTokenConfigured ? "Saved securely — paste only to replace" : "Paste the generated access token"}
                            />
                            <small className="meta-capi-security">
                              <FaShieldAlt /> {settings.metaConversionsApiTokenConfigured
                                ? "Token is encrypted and stored. It is never returned to this browser."
                                : "The token will be encrypted before it is stored."}
                            </small>

                            <label className="form-label fw-semibold small mt-3" htmlFor="metaConversionsApiTestEventCode">
                              Test Event Code
                            </label>
                            <input
                              id="metaConversionsApiTestEventCode"
                              type="text"
                              name="metaConversionsApiTestEventCode"
                              className="form-control font-monospace"
                              value={settings.metaConversionsApiTestEventCode || ""}
                              onChange={handleInput}
                              placeholder="e.g. TEST12345"
                              maxLength="100"
                            />
                            <small className="text-muted d-block mt-2">Find this in Events Manager → Test events. It is used only by the test button.</small>
                          </div>

                          <div className="meta-capi-steps">
                            <strong>Generate your access token</strong>
                            <ol>
                              <li>Open Events Manager and select the same Pixel/Dataset ID entered above.</li>
                              <li>Open <b>Settings</b> and scroll to <b>Conversions API</b>.</li>
                              <li>Choose <b>Set up manually</b>, then <b>Generate access token</b>.</li>
                              <li>Paste the token here, enable server tracking, and save.</li>
                              <li>Copy the Test Event Code, save again, then send a test event.</li>
                            </ol>
                            <div className="meta-capi-note">
                              Browser and server Purchase events share <code>purchase_orderId</code>, so Meta counts one purchase instead of two.
                            </div>
                          </div>
                        </div>

                        <div className="meta-capi-actions">
                          <span><FaCheckCircle /> Sends value, currency, products and privacy-safe hashed customer matching data.</span>
                          <div>
                            <button
                              type="button"
                              className="is-secondary"
                              onClick={handleSave}
                              disabled={saving || !settings.metaConversionsApiEnabled || (!settings.metaConversionsApiTokenConfigured && !String(settings.metaConversionsApiAccessToken || "").trim())}
                            >
                              <FaSave /> {saving ? "Saving…" : "Save server setup"}
                            </button>
                            <button
                              type="button"
                              onClick={testMetaConversions}
                              disabled={testingMetaCapi || !settings.metaConversionsApiEnabled || !settings.metaConversionsApiTokenConfigured || !String(settings.metaConversionsApiTestEventCode || "").trim()}
                            >
                              <FaChartLine /> {testingMetaCapi ? "Sending…" : "Send test event"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
          </div>
          </div>
          </div>
        </div>

        {/* Bottom spacing so content clears the floating FAB dock */}
        <div className="mb-5 pb-4" />
      </form>

      {/* Floating Save Action Dock — Always in Screen View */}
      <div
        className="store-settings-save-dock d-flex align-items-center justify-content-between gap-3 shadow-lg border"
        style={{
          position: "fixed",
          bottom: "22px",
          right: "28px",
          zIndex: 1040,
          backgroundColor: "rgba(15, 23, 42, 0.94)",
          backdropFilter: "blur(12px)",
          color: "#ffffff",
          padding: "8px 16px 8px 18px",
          borderRadius: "50px",
          boxShadow: "0 20px 40px -8px rgba(0, 0, 0, 0.4), 0 8px 16px -4px rgba(0, 0, 0, 0.2)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          maxWidth: "calc(100vw - 40px)",
        }}
      >
        <div className="store-settings-save-status d-flex align-items-center gap-2 me-1">
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: saving ? "#f59e0b" : "#10b981",
              boxShadow: saving
                ? "0 0 10px rgba(245, 158, 11, 0.8)"
                : "0 0 10px rgba(16, 185, 129, 0.8)",
              display: "inline-block",
            }}
          />
          <span className="d-none d-sm-inline" style={{ fontSize: "13px", fontWeight: "600", color: "#f8fafc" }}>
            {saving ? "Saving Changes..." : "Store Config"}
          </span>
          <span
            className="d-none d-md-inline badge bg-dark text-muted border border-secondary px-2 py-1 ms-1"
            style={{ fontSize: "10px", fontWeight: "normal", letterSpacing: "0.4px" }}
            title="Press Ctrl+S or Cmd+S to save"
          >
            Ctrl+S
          </span>
        </div>

        <div className="store-settings-save-actions d-flex align-items-center gap-2">
          <button
            type="button"
            onClick={fetchSettings}
            disabled={saving}
            className="btn btn-sm btn-outline-light rounded-pill px-3 py-1"
            style={{
              fontSize: "12px",
              borderColor: "rgba(255, 255, 255, 0.25)",
              color: "#cbd5e1",
            }}
            title="Discard changes & reload latest settings"
          >
            <FaUndo size={11} className="me-1" /> Reload
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn btn-sm rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2 text-white shadow"
            style={{
              fontSize: "13px",
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              border: "none",
            }}
          >
            <FaSave size={13} />
            <span className="d-none d-sm-inline">{saving ? "Saving Changes..." : "Save Store Configuration"}</span>
            <span className="d-sm-none">{saving ? "Saving..." : "Save changes"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
